import { useCallback, useEffect } from 'react'
import useSWR, { type SWRConfiguration, useSWRConfig } from 'swr'

import { clearRetryBudgetSpent, markRetryBudgetSpent, useRetryBudgetSpent } from '@/lib/retryBudget'
import { ApiError, createFetcher } from '@/services/api'
import { userService } from '@/services/userService'
import type { User } from '@/types/user'
import { useAuthToken } from './useAuthToken'

// Delay before each retry, and by its length the retry budget. SWR's default
// schedule is unbounded (`errorRetryCount` unset) and backs off from 5s, which is
// far too long for a request that holds the whole authenticated app behind a
// spinner: a genuinely offline learner has to reach the retry page quickly. Two
// short attempts absorb the single dropped request behind most failures here.
//
// This table is the only retry budget for the key: `errorRetryCount` is read
// solely by SWR's default `onErrorRetry`, which the handler below replaces, and
// the other subscriber to this key (`useTrialAcAutoRefresh`) opts out of retrying
// so it can never start a run on SWR's default schedule instead.
const RETRY_DELAYS_MS = [1000, 3000]

// Failsafe deadline for spending the budget, measured from the first failure.
// `onErrorRetry` is otherwise the only thing that can spend it, which makes the
// spinner depend on the retry chain staying with an instance this hook
// configured. SWR dispatches each retry to `EVENT_REVALIDATORS[key][0]`, the
// first-registered subscriber, and nothing enforces that that is this one: today
// it holds only because React flushes child layout effects before the parent's.
// If the chain ever ends elsewhere the learner would sit on the verifying
// spinner forever, with no retry button and no event, which is strictly worse
// than the flash this hook exists to remove. Comfortably clears the delays above
// plus three request round trips, so it never pre-empts the normal path.
const RETRY_BUDGET_DEADLINE_MS = 8000

export function useCurrentUser() {
  const { getToken, isSignedIn } = useAuthToken()
  const fetcher = createFetcher(getToken)
  const key = isSignedIn ? userService.getCurrentUserUrl() : null
  // A transport failure isn't final until SWR stops retrying. Acting on the
  // first one blamed a learner's single dropped packet for a load that usually
  // recovers on the next attempt. Scoped to the key, not this component, so
  // every subscriber sees the same verdict.
  const retryBudgetSpent = useRetryBudgetSpent(key)

  const onErrorRetry = useCallback<NonNullable<SWRConfiguration['onErrorRetry']>>(
    (_error, errorKey, _config, revalidate, opts) => {
      // SWR counts from 1 on the first retry, so this indexes the next delay.
      const retryCount = opts.retryCount ?? 1
      if (retryCount === 1) {
        // A fresh run: reopen the grace period. Without this the budget is
        // one-shot, and the retry button (or a later sign-in in the same
        // document) reports its very first attempt as a final failure.
        clearRetryBudgetSpent(errorKey)
      }
      const delay = RETRY_DELAYS_MS[retryCount - 1]
      if (delay === undefined) {
        markRetryBudgetSpent(errorKey)
        return
      }
      setTimeout(() => revalidate(opts), delay)
    },
    []
  )

  const onSuccess = useCallback((_data: User, successKey: string) => {
    clearRetryBudgetSpent(successKey)
  }, [])

  const { data, error, isLoading, mutate } = useSWR<User>(key, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60000,
    onErrorRetry,
    onSuccess,
  })

  // A completed HTTP exchange is always raised as an `ApiError` carrying its
  // status, so anything else could not have reached the server. Preferred over
  // matching the browser's wording, which is a denylist that silently drops the
  // grace period for anything it hasn't seen (Safari's "The Internet connection
  // appears to be offline.", an abort) and misfires on a backend `detail` that
  // happens to contain the same phrase.
  //
  // The converse does not hold: `createAuthenticatedRequest` also raises a
  // synthetic `ApiError(401)` when `getToken()` yields nothing, which includes
  // Clerk failing to refresh an expired JWT while offline. That learner is
  // offline but lands on `not_found` rather than the retry path. Pre-existing,
  // and fixing it means giving that case its own error type in `api.ts` rather
  // than borrowing a status no server sent.
  const apiError = error instanceof ApiError ? error : null
  const isNetworkFailure = error !== undefined && apiError === null

  useEffect(() => {
    if (key === null || !isNetworkFailure || retryBudgetSpent) return
    const timer = setTimeout(() => markRetryBudgetSpent(key), RETRY_BUDGET_DEADLINE_MS)
    return () => clearTimeout(timer)
  }, [key, isNetworkFailure, retryBudgetSpent])

  return {
    user: data,
    isLoading,
    error: error?.message || null,
    errorStatus: apiError?.status ?? null,
    isNetworkFailure,
    // Only a request that never landed is worth waiting on: an API error came
    // back with a status, so retrying it won't change the answer.
    isRetryPending: isNetworkFailure && !retryBudgetSpent,
    refetch: mutate,
  }
}

/** Revalidates `/users/me` for every subscriber without subscribing the caller. */
export function useRevalidateCurrentUser(): () => void {
  const { mutate } = useSWRConfig()
  return useCallback(() => {
    void mutate(userService.getCurrentUserUrl())
  }, [mutate])
}
